import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Button, Pagination
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  Add as AddIcon, 
  Work as WorkIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useAuth } from '../context/authContext';
import { getServicesByCompany, createService, deleteService, updateServiceWithPhoto } from '../services/serviceService';
import Swal from 'sweetalert2';

import ProfileCard from '../components/services/myservice/ProfileCard';
import ServiceCard from '../components/services/myservice/myServiceCard';
import ServiceDialog from '../components/services/myservice/ServiceDialog';
import { PageLoader } from '../components/loader';

import {Banner} from '../components/common/Banner';
import SectionTips from '../components/common/SectionTips';
import ArrowBack from '../components/common/ArrowBack';

// Configuración global de SweetAlert2
Swal.mixin({
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  toast: true,
  background: '#ffffff',
  backdrop: false,
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer)
    toast.addEventListener('mouseleave', Swal.resumeTimer)
  }
});

const MyServices = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    location: "",
    bio: ""
  });
  
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentService, setCurrentService] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [page, setPage] = useState(1);
  const [showInfo, setShowInfo] = useState(false);
  const itemsPerPage = 2;

  const navigate = useNavigate();

  const initialServiceState = {
    title: "",
    description: "",
    type_publish: "Servicio",
    type_service: "Oferta",
    category: "",
    date_publish: new Date().toISOString().split('T')[0],
    contact: "",
    status: "active",
    company_rut: user?.rut || "",
    email: user?.email || "",
    location: "",
    image: null,
    visits: 0,
    name_contact: user?.name || ""
  };
  const [serviceForm, setServiceForm] = useState(initialServiceState);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (!user || !user.rut) {
          throw new Error('No se encontró información de la compañía');
        }

        const servicesData = await getServicesByCompany(user.rut, user);
        // Ordenar servicios por fecha de publicación (más reciente primero)
        const sortedServices = [...(servicesData || [])].sort((a, b) => {
          const dateA = new Date(a.date_publish || new Date());
          const dateB = new Date(b.date_publish || new Date());
          return dateB - dateA;
        });
        setServices(sortedServices);  
        
        setProfile({
          name: user.name || "",
          company: user.companyName || "",
          email: user.email || "",
          phone: user.phone || "",
          location: user.address || "",
          bio: user.bio || ""
        });
        
        setDataLoaded(true);
      } catch (err) {
        if (err.message !== "No se encontraron servicios para esta compañía") {
          showErrorAlert(err.message || 'Error al cargar los servicios');
        }
        setServices([]); 
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      fetchData();
    }
  }, [user]);

  const showSuccessAlert = (message) => {
    Swal.fire({
      icon: 'success',
      title: message,
      position: 'top',
      showConfirmButton: false,
      timer: 2000,
      background: '#ffffff'
    });
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: message,
      position: 'top',
      confirmButtonColor: '#C42251',
      background: '#ffffff'
    });
  };

  const pageCount = Math.ceil(services.length / itemsPerPage);
  const paginatedServices = services.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setServiceForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setServiceForm(prev => ({ ...prev, image: file }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpenDialog = (service = null) => {
    setCurrentService(service);
    if (service) {
      setServiceForm({
        ...service, 
        image: null 
      });
      setPreview(service.image || null);
    } else {
      setServiceForm({
        ...initialServiceState,
        company_rut: user?.rut || "",
        email: user?.email || "",
        name_contact: user?.name || ""
      });
      setPreview(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentService(null);
    setServiceForm(initialServiceState);
    setPreview(null);
    setPhoto(null);
  };

  const handleSaveService = async () => {
    try {
      setLoading(true);
      
      if (!user || !user.rut) {
        throw new Error('No se pudo identificar la compañía');
      }

      const serviceData = {
        title: serviceForm.title,
        description: serviceForm.description,
        type_publish: serviceForm.type_publish,
        type_service: serviceForm.type_service,
        category: serviceForm.category,
        date_publish: serviceForm.date_publish,
        contact: serviceForm.contact,
        status: serviceForm.status,
        company_rut: user.rut,
        email: serviceForm.email,
        location: serviceForm.location,
        visits: serviceForm.visits,
        name_contact: serviceForm.name_contact
      };

      if (currentService) {
        const updatedService = await updateServiceWithPhoto(
          currentService.id, 
          serviceData,
          photo,
          user,
        );
        
        // Actualizar manteniendo el orden
        setServices(prevServices => {
          const updated = prevServices.map(s => s.id === currentService.id ? updatedService : s);
          return updated.sort((a, b) => {
            const dateA = new Date(a.date_publish || new Date());
            const dateB = new Date(b.date_publish || new Date());
            return dateB - dateA;
          });
        });
        showSuccessAlert('Servicio actualizado correctamente');
      } else {
        const newService = await createService(
          serviceData,
          photo,
          user
        );
        
        // Agregar nuevo servicio manteniendo el orden
        setServices(prevServices => {
          const updated = [...prevServices, newService];
          return updated.sort((a, b) => {
            const dateA = new Date(a.date_publish || new Date());
            const dateB = new Date(b.date_publish || new Date());
            return dateB - dateA;
          });
        });
        showSuccessAlert('Servicio creado exitosamente');
      }
      
      handleCloseDialog();
    } catch (err) {
      showErrorAlert(err.message || 'Ocurrió un error al guardar el servicio');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = async (id) => {
    try {
      setLoading(true);
      await deleteService(id, user);
      setServices(prevServices => {
        const updated = prevServices.filter(service => service.id !== id);
        return updated.sort((a, b) => {
          const dateA = new Date(a.date_publish || new Date());
          const dateB = new Date(b.date_publish || new Date());
          return dateB - dateA;
        });
      });
      showSuccessAlert('Servicio eliminado correctamente');
    } catch (err) {
      showErrorAlert(err.message || 'Ocurrió un error al eliminar el servicio');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  if (loading && !dataLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <PageLoader message="Cargando tus servicios..." />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%)', 
      py: 4 
    }}>
      <Box sx={{ 
        maxWidth: 'lg', 
        mx: 'auto', 
        px: { xs: 2, md: 4 },
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box>
          <ArrowBack></ArrowBack>
        </Box>
        <Banner title="Mis Publicaciones" message="Administra tus servicios"/>
        <SectionTips
          buttonTitle='¿Como usar esta sección?'
          section='mis servicios'
          tipsTitle="Consejos para publicar"
          tipsList={[
            "Puedes publicar servicios de compra o venta.",
            "Asegúrate de incluir una descripción clara y concisa.",
          ]}
          finalNote='Recuerda que puedes editar o eliminar tus publicaciones en cualquier momento.'
        />

        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4
        }}>
          <Box sx={{ 
            width: { md: 350 },
            position: { md: 'sticky' },
            top: 20,
            alignSelf: 'flex-start'
          }}>
            <ProfileCard 
              profile={profile}
              loading={loading}
            />
            
            <Button 
              fullWidth 
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              disabled={loading}
              sx={{ 
                mt: 1,
                background: '#1BBB55',
                color: 'white',
                '&:hover': { 
                  background: '#071D3A'
                },
                py: 1.5,
                borderRadius: '12px'
              }}
            >
              Crear Nueva Publicación
            </Button>
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Paper elevation={3} sx={{ 
              borderRadius: '12px',
              overflow: 'hidden',
              mb: 3
            }}>
              <Box sx={{ 
                background: 'linear-gradient(to bottom, #09274A, #0488AF)',
                p: 3,
                color: 'white'
              }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <WorkIcon /> Mis Publicaciones
                </Typography>
              </Box>
              
              <Box sx={{ p: 3 }}>
                {services.length > 0 ? (
                  <>
                    <Box sx={{ '& > *:not(:last-child)': { mb: 3 } }}>
                      {paginatedServices.map(service => (
                        <ServiceCard
                          key={service.id}
                          service={service}
                          loading={loading}
                          onEditClick={handleOpenDialog}
                          onDeleteClick={handleDeleteService}
                        />
                      ))}
                    </Box>
                    
                    {pageCount > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination
                          count={pageCount}
                          page={page}
                          onChange={handlePageChange}
                          color="primary"
                          size="small"
                          siblingCount={0}
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    py: 4,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" sx={{ 
                      color: 'text.secondary',
                      mb: 2
                    }}>
                      Comienza a agregar tus servicios
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      color: 'text.secondary',
                      mb: 3,
                      maxWidth: '400px'
                    }}>
                      Publica tu primer servicio para que otros usuarios puedan conocer tus ofertas o necesidades comerciales.
                    </Typography>
                    <Button 
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog()}
                      disabled={loading}
                      sx={{ 
                        background: '#0488AF',
                        color: 'white',
                        '&:hover': { 
                          background: '#03779C'
                        }
                      }}
                    >
                      Crear Publicación
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      <ServiceDialog
        open={openDialog}
        currentService={currentService}
        serviceForm={serviceForm}
        loading={loading}
        preview={preview}
        onClose={handleCloseDialog}
        onSave={handleSaveService}
        onInputChange={handleInputChange}
        onPhotoChange={handlePhotoChange}
      />
    </Box>
  );
};

export default MyServices;